// src/taskpane/components/TranslationPanel.tsx
import * as React from "react";
import {
  ProgressBar,
  MessageBar,
  MessageBarBody,
  Button,
  makeStyles,
  tokens,
  Divider,
  Text,
  Badge,
} from "@fluentui/react-components";
import {
  CheckmarkCircle24Regular,
  DismissCircle24Regular,
  ArrowDownload24Regular,
} from "@fluentui/react-icons";
import { ExtractionResult, TranslationResult } from "../services/types";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  statsRow: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    alignItems: "center",
    flexWrap: "wrap",
  },
  progressSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  resultSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  resultItem: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  textPair: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  actions: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalS,
  },
});

export type Phase =
  | "idle"
  | "extracting"
  | "extracted"
  | "translating"
  | "translated"
  | "replacing"
  | "done"
  | "error";

interface TranslationPanelProps {
  phase: Phase;
  extractionResult: ExtractionResult | null;
  translationResult: TranslationResult | null;
  progress: { completed: number; total: number; current: string };
  errorMessage: string | null;
  modeLabel?: string;
  onStartTranslation: () => void;
  onApplyTranslation: () => void;
  onReset: () => void;
}

export default function TranslationPanel({
  phase,
  extractionResult,
  translationResult,
  progress,
  errorMessage,
  modeLabel,
  onStartTranslation,
  onApplyTranslation,
  onReset,
}: TranslationPanelProps) {
  const styles = useStyles();

  if (phase === "idle") {
    return null;
  }

  const progressPercent =
    progress.total > 0
      ? Math.round((progress.completed / progress.total) * 100)
      : 0;

  return (
    <div className={styles.root}>
      {/* 错误提示 */}
      {errorMessage && phase === "error" && (
        <MessageBar intent="error">
          <MessageBarBody>{errorMessage}</MessageBarBody>
        </MessageBar>
      )}

      {/* 提取结果统计 */}
      {extractionResult && (
        <div className={styles.statsRow}>
          <Text size={200} weight="semibold">
            {modeLabel ? `${modeLabel} · ` : ""}扫描结果
          </Text>
          <Badge appearance="informative" size="small">
            {extractionResult.totalSlides} 张幻灯片
          </Badge>
          <Badge appearance="informative" size="small">
            {extractionResult.totalTextItems} 个文本项
          </Badge>
          <Badge appearance="informative" size="small">
            {extractionResult.totalCharacters.toLocaleString()} 字符
          </Badge>
        </div>
      )}

      {/* 翻译进度 */}
      {(phase === "translating" || phase === "replacing") && (
        <div className={styles.progressSection}>
          <Text size={200}>
            {phase === "translating" ? "正在翻译..." : "正在替换文本..."}
          </Text>
          <ProgressBar value={progressPercent} />
          <Text
            size={100}
            style={{ color: tokens.colorNeutralForeground3 }}
          >
            {progress.completed} / {progress.total}
            {progress.current && ` — "${progress.current}..."`}
          </Text>
        </div>
      )}

      {/* 翻译完成统计 */}
      {translationResult &&
        (phase === "translated" || phase === "done") && (
          <div className={styles.resultSection}>
            <Divider>翻译结果</Divider>
            <div className={styles.statsRow}>
              <Badge
                appearance="success"
                icon={<CheckmarkCircle24Regular />}
              >
                成功 {translationResult.successCount}
              </Badge>
              {translationResult.failCount > 0 && (
                <Badge
                  appearance="danger"
                  icon={<DismissCircle24Regular />}
                >
                  失败 {translationResult.failCount}
                </Badge>
              )}
              {translationResult.skippedCount > 0 && (
                <Badge appearance="informative">
                  跳过 {translationResult.skippedCount}
                </Badge>
              )}
            </div>

            {/* 翻译预览（前 5 项） */}
            {translationResult.items
              .filter((i) => i.translated)
              .slice(0, 5)
              .map((item) => (
                <div key={item.id} className={styles.resultItem}>
                  <Text
                    size={100}
                    style={{ color: tokens.colorNeutralForeground3 }}
                  >
                    {item.shapeName}
                  </Text>
                  <div className={styles.textPair}>
                    <Text size={150} strike>
                      {item.originalText.substring(0, 100)}
                      {item.originalText.length > 100 ? "..." : ""}
                    </Text>
                    <Text size={150} weight="semibold">
                      {item.translatedText.substring(0, 100)}
                      {item.translatedText.length > 100 ? "..." : ""}
                    </Text>
                  </div>
                </div>
              ))}
          </div>
        )}

      {/* 操作按钮 */}
      <div className={styles.actions}>
        {phase === "extracted" && (
          <Button appearance="primary" onClick={onStartTranslation}>
            开始翻译
          </Button>
        )}
        {phase === "translated" && (
          <Button
            appearance="primary"
            icon={<ArrowDownload24Regular />}
            onClick={onApplyTranslation}
          >
            应用翻译到演示文稿
          </Button>
        )}
        {phase === "done" && (
          <MessageBar intent="success">
            <MessageBarBody>
              翻译已成功应用到演示文稿！所有格式属性已保留。
            </MessageBarBody>
          </MessageBar>
        )}
        {(phase === "done" || phase === "error" || phase === "translated") && (
          <Button appearance="secondary" onClick={onReset}>
            重新开始
          </Button>
        )}
      </div>
    </div>
  );
}
